import React, { useState } from "react";
import { LandingTopBar } from "../components/landing/LandingTopBar";
import { Button, Checkbox, Input, Label, ListBox, Select } from "@heroui/react";
import { AnimatePresence, motion } from "motion/react";
import { ArrowLeft, ArrowRight, Building2, LogIn, Plus } from "lucide-react";

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
}

function portalDataset() {
  return document.getElementById("portal-root")?.dataset || {};
}

function AuthFlash() {
  const { flashNotice, flashAlert } = portalDataset();
  if (!flashNotice && !flashAlert) return null;

  return (
    <div className={`auth-flash ${flashAlert ? "auth-flash--alert" : "auth-flash--notice"}`} role="status">
      {flashAlert || flashNotice}
    </div>
  );
}

function AuthShell({ eyebrow, title, children }) {
  return (
    <div className="landing-root">
      <div className="landing-grain" />
      <LandingTopBar hidden={false} />
      <div className="landing-scroll">
        <section id="home" className="landing-section landing-hero is-visible auth-hero">
          <motion.div
            className="auth-card auth-card--sohub auth-card--in-portal"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 0.68, 0, 1] }}
          >
            <p className="auth-card__eyebrow">{eyebrow}</p>
            <h2 className="auth-card__title">{title}</h2>
            <AuthFlash />
            {children}
          </motion.div>
        </section>
      </div>
    </div>
  );
}

export function SignInPage() {
  return (
    <AuthShell eyebrow="Say hi" title="Sign in to your portal">
      <form className="auth-card__form" method="post" action="/users/sign_in">
        <input type="hidden" name="authenticity_token" value={csrfToken()} />
        <div className="auth-card__field">
          <Input
            autoFocus
            autoComplete="email"
            placeholder="Email"
            type="email"
            name="user[email]"
            id="user_email"
            variant="bordered"
            radius="md"
          />
        </div>
        <div className="auth-card__field">
          <Input
            autoComplete="current-password"
            placeholder="Password"
            type="password"
            name="user[password]"
            id="user_password"
            variant="bordered"
            radius="md"
          />
        </div>
        <div className="auth-card__remember">
          <Checkbox name="user[remember_me]" id="user_remember_me" value="1" radius="sm">
            Remember me
          </Checkbox>
        </div>
        <div className="auth-card__actions">
          <Button type="button" onPress={() => { window.location.href = "/portal/signup"; }} className="auth-pill-btn auth-pill-btn--ghost">
            <span className="auth-pill-btn__icon" aria-hidden="true">
              <Plus size={14} />
            </span>
            <span>Sign up</span>
          </Button>
          <Button type="submit" className="auth-pill-btn auth-pill-btn--primary">
            <span>Sign in</span>
            <span className="auth-pill-btn__icon" aria-hidden="true">
              <ArrowRight size={14} />
            </span>
          </Button>
        </div>
      </form>
      <div className="auth-links auth-links--sohub">
        <a href="/portal/forgot-password">Forgot your password?</a>
      </div>
    </AuthShell>
  );
}

export function SignUpPage() {
  const [locale, setLocale] = useState("de");
  const [platform, setPlatform] = useState("shopify");
  const [step, setStep] = useState(0);
  const [companyName, setCompanyName] = useState("");
  const [slug, setSlug] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const maxStep = 2;
  const handleSignUpSubmit = (event) => {
    if (step < maxStep) {
      event.preventDefault();
      setStep((currentStep) => Math.min(maxStep, currentStep + 1));
      return;
    }

    if (!password || !passwordConfirmation) {
      event.preventDefault();
    }
  };

  return (
    <AuthShell eyebrow="Get started" title="Create your portal account">
      <form className="auth-card__form" method="post" action="/users" onSubmit={handleSignUpSubmit}>
        <input type="hidden" name="authenticity_token" value={csrfToken()} />
        <input type="hidden" name="shop[company_name]" value={companyName} />
        <input type="hidden" name="shop[slug]" value={slug} />
        <input type="hidden" name="shop[default_locale]" value={locale} />
        <input type="hidden" name="user[first_name]" value={firstName} />
        <input type="hidden" name="user[last_name]" value={lastName} />
        <input type="hidden" name="user[email]" value={email} />
        <input type="hidden" name="user[password]" value={password} />
        <input type="hidden" name="user[password_confirmation]" value={passwordConfirmation} />
        <div className="auth-stepper" aria-label="Signup progress">
          <span className={step === 0 ? "is-active" : ""}>Business</span>
          <span className={step === 1 ? "is-active" : ""}>Profile</span>
          <span className={step === 2 ? "is-active" : ""}>Security</span>
        </div>
        <div className="auth-step-shell">
          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div
                key="step-business"
                className="auth-step"
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.35, ease: [0.22, 0.68, 0, 1] }}
              >
                <div className="auth-card__field">
                  <Input
                    placeholder="Company / store name"
                    type="text"
                    required
                    value={companyName}
                    onChange={(event) => setCompanyName(event.target.value)}
                    variant="bordered"
                    radius="md"
                  />
                </div>
                <div className="auth-card__field">
                  <Input
                    placeholder="Store URL slug (optional)"
                    type="text"
                    value={slug}
                    onChange={(event) => setSlug(event.target.value)}
                    variant="bordered"
                    radius="md"
                  />
                </div>
                <div className="auth-card__field">
                  <Select
                    placeholder="Commerce platform"
                    value={platform}
                    onChange={setPlatform}
                    fullWidth
                  >
                    <Label>Commerce platform</Label>
                    <Select.Trigger>
                      <Select.Value />
                      <Select.Indicator />
                    </Select.Trigger>
                    <Select.Popover>
                      <ListBox>
                        <ListBox.Item id="shopify" textValue="Shopify">Shopify<ListBox.ItemIndicator /></ListBox.Item>
                        <ListBox.Item id="woocommerce" textValue="WooCommerce">WooCommerce<ListBox.ItemIndicator /></ListBox.Item>
                        <ListBox.Item id="custom" textValue="Custom storefront">Custom storefront<ListBox.ItemIndicator /></ListBox.Item>
                      </ListBox>
                    </Select.Popover>
                  </Select>
                </div>
              </motion.div>
            )}
            {step === 1 && (
              <motion.div
                key="step-profile"
                className="auth-step"
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.35, ease: [0.22, 0.68, 0, 1] }}
              >
                <div className="auth-card__field">
                  <Input
                    placeholder="First name"
                    type="text"
                    value={firstName}
                    onChange={(event) => setFirstName(event.target.value)}
                    variant="bordered"
                    radius="md"
                  />
                </div>
                <div className="auth-card__field">
                  <Input
                    placeholder="Last name"
                    type="text"
                    value={lastName}
                    onChange={(event) => setLastName(event.target.value)}
                    variant="bordered"
                    radius="md"
                  />
                </div>
                <div className="auth-card__field">
                  <Input
                    placeholder="Email"
                    type="email"
                    required
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    variant="bordered"
                    radius="md"
                  />
                </div>
                <div className="auth-card__field">
                  <Select
                    placeholder="Default locale"
                    value={locale}
                    onChange={setLocale}
                    fullWidth
                  >
                    <Label>Default locale</Label>
                    <Select.Trigger>
                      <Select.Value />
                      <Select.Indicator />
                    </Select.Trigger>
                    <Select.Popover>
                      <ListBox>
                        <ListBox.Item id="de" textValue="German">German<ListBox.ItemIndicator /></ListBox.Item>
                        <ListBox.Item id="en" textValue="English">English<ListBox.ItemIndicator /></ListBox.Item>
                        <ListBox.Item id="fr" textValue="French">French<ListBox.ItemIndicator /></ListBox.Item>
                        <ListBox.Item id="es" textValue="Spanish">Spanish<ListBox.ItemIndicator /></ListBox.Item>
                        <ListBox.Item id="it" textValue="Italian">Italian<ListBox.ItemIndicator /></ListBox.Item>
                      </ListBox>
                    </Select.Popover>
                  </Select>
                </div>
              </motion.div>
            )}
            {step === 2 && (
              <motion.div
                key="step-security"
                className="auth-step"
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.35, ease: [0.22, 0.68, 0, 1] }}
              >
                <div className="auth-card__field">
                  <Input
                    placeholder="Password"
                    type="password"
                    required
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    variant="bordered"
                    radius="md"
                  />
                </div>
                <div className="auth-card__field">
                  <Input
                    placeholder="Confirm password"
                    type="password"
                    required
                    value={passwordConfirmation}
                    onChange={(event) => setPasswordConfirmation(event.target.value)}
                    variant="bordered"
                    radius="md"
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div className="auth-card__actions">
          {step === 0 ? (
            <Button type="button" onPress={() => { window.location.href = "/portal/signin"; }} className="auth-pill-btn auth-pill-btn--ghost">
              <span className="auth-pill-btn__icon" aria-hidden="true">
                <LogIn size={14} />
              </span>
              <span>Sign in</span>
            </Button>
          ) : (
            <Button type="button" onPress={() => setStep((s) => Math.max(0, s - 1))} className="auth-pill-btn auth-pill-btn--ghost">
              <span className="auth-pill-btn__icon" aria-hidden="true">
                <ArrowLeft size={14} />
              </span>
              <span>Back</span>
            </Button>
          )}
          {step < maxStep ? (
            <Button type="button" onPress={() => setStep((s) => Math.min(maxStep, s + 1))} className="auth-pill-btn auth-pill-btn--primary">
              <span>Next</span>
              <span className="auth-pill-btn__icon" aria-hidden="true">
                <ArrowRight size={14} />
              </span>
            </Button>
          ) : (
            <Button type="submit" className="auth-pill-btn auth-pill-btn--primary">
              <span>Create account</span>
              <span className="auth-pill-btn__icon" aria-hidden="true">
                <Building2 size={14} />
              </span>
            </Button>
          )}
        </div>
      </form>
    </AuthShell>
  );
}

export function ForgotPasswordPage() {
  return (
    <AuthShell eyebrow="Need access?" title="Reset your password">
      <form className="auth-card__form" method="post" action="/users/password">
        <input type="hidden" name="authenticity_token" value={csrfToken()} />
        <div className="auth-card__field">
          <Input placeholder="Email" type="email" name="user[email]" required variant="bordered" radius="md" />
        </div>
        <div className="auth-card__actions">
          <Button type="button" onPress={() => { window.location.href = "/portal/signin"; }} className="auth-pill-btn auth-pill-btn--ghost">
            <span className="auth-pill-btn__icon" aria-hidden="true">
              <LogIn size={14} />
            </span>
            <span>Back to sign in</span>
          </Button>
          <Button type="submit" className="auth-pill-btn auth-pill-btn--primary">
            <span>Send reset link</span>
            <span className="auth-pill-btn__icon" aria-hidden="true">
              <ArrowRight size={14} />
            </span>
          </Button>
        </div>
      </form>
    </AuthShell>
  );
}

export function VerifyEmailPage() {
  const pendingEmail = portalDataset().pendingEmail;

  React.useEffect(() => {
    if (!pendingEmail) {
      window.location.href = "/portal/signin";
    }
  }, [pendingEmail]);

  if (!pendingEmail) return null;

  return (
    <AuthShell eyebrow="Almost there" title="Verify your email">
      <p className="auth-card__hint">Enter the 6-digit code we sent to {pendingEmail}.</p>
      <form className="auth-card__form" method="post" action="/portal/verify-email">
        <input type="hidden" name="authenticity_token" value={csrfToken()} />
        <div className="auth-card__field">
          <Input
            autoFocus
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="Verification code"
            name="verification[otp]"
            required
            variant="bordered"
            radius="md"
          />
        </div>
        <div className="auth-card__actions">
          <Button type="button" onPress={() => { window.location.href = "/portal/signin"; }} className="auth-pill-btn auth-pill-btn--ghost">
            <span className="auth-pill-btn__icon" aria-hidden="true">
              <LogIn size={14} />
            </span>
            <span>Back to sign in</span>
          </Button>
          <Button type="submit" className="auth-pill-btn auth-pill-btn--primary">
            <span>Verify email</span>
            <span className="auth-pill-btn__icon" aria-hidden="true">
              <ArrowRight size={14} />
            </span>
          </Button>
        </div>
      </form>
      <form className="auth-links auth-links--sohub" method="post" action="/portal/verify-email/resend">
        <input type="hidden" name="authenticity_token" value={csrfToken()} />
        <button type="submit" className="auth-inline-link">Resend code</button>
      </form>
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  const resetPasswordToken = portalDataset().resetPasswordToken || new URLSearchParams(window.location.search).get("reset_password_token");

  React.useEffect(() => {
    if (!resetPasswordToken) {
      window.location.href = "/portal/forgot-password";
    }
  }, [resetPasswordToken]);

  if (!resetPasswordToken) return null;

  return (
    <AuthShell eyebrow="Choose a new password" title="Reset your password">
      <form className="auth-card__form" method="post" action="/users/password">
        <input type="hidden" name="authenticity_token" value={csrfToken()} />
        <input type="hidden" name="_method" value="put" />
        <input type="hidden" name="user[reset_password_token]" value={resetPasswordToken} />
        <div className="auth-card__field">
          <Input
            autoFocus
            autoComplete="new-password"
            placeholder="New password"
            type="password"
            name="user[password]"
            required
            variant="bordered"
            radius="md"
          />
        </div>
        <div className="auth-card__field">
          <Input
            autoComplete="new-password"
            placeholder="Confirm new password"
            type="password"
            name="user[password_confirmation]"
            required
            variant="bordered"
            radius="md"
          />
        </div>
        <div className="auth-card__actions">
          <Button type="button" onPress={() => { window.location.href = "/portal/signin"; }} className="auth-pill-btn auth-pill-btn--ghost">
            <span className="auth-pill-btn__icon" aria-hidden="true">
              <LogIn size={14} />
            </span>
            <span>Back to sign in</span>
          </Button>
          <Button type="submit" className="auth-pill-btn auth-pill-btn--primary">
            <span>Update password</span>
            <span className="auth-pill-btn__icon" aria-hidden="true">
              <ArrowRight size={14} />
            </span>
          </Button>
        </div>
      </form>
    </AuthShell>
  );
}
